import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const token = JSON.parse(localStorage.getItem("token"))?.token;

const baseUrl = process.env.REACT_APP_SERVER_URL;
const xAuthToken = token ? token : null;

const secretKey = process.env.REACT_APP_SECRET_KEY;

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/api`,
    prepareHeaders: (headers) => {
      const xAuthToken = JSON.parse(localStorage.getItem("token"))?.token;
      const secretKey = process.env.REACT_APP_SECRET_KEY;
      if (xAuthToken) {
        headers.set("x-auth-token", xAuthToken);
      }
      if (secretKey) {
        headers.set("secret-key", secretKey);
      }
      headers.set("Content-Type", "application/json"); // Set Content-Type if needed

      const headersObj = {};
      headers.forEach((value, key) => {
        headersObj[key] = value;
      });
      // console.log("Prepared Headers:", headersObj);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    fetchAllPayment: builder.query({ query: () => "stripe/products-list" }),
    fetchAllNewsNet: builder.query({ query: () => "top-net-link/get-all" }),
    fetchAllNews: builder.query({ query: () => "news/get-all" }),
    fetchSingleNews: builder.query({ query: (id) => `news/get-single/${id}` }),

    createNewsNet: builder.mutation({
      query: (newsData) => ({
        url: "top-net-link/create",
        method: "POST",
        body: newsData,
      }),
    }),
    updateNewsNet: builder.mutation({
      query: ({ id, newsData }) => ({
        url: `top-net-link/update/${id}`,
        method: "PUT",
        body: newsData,
      }),
    }),
    deleteNewsNet: builder.mutation({
      query: (id) => ({ url: `top-net-link/delete/${id}`, method: "DELETE" }),
    }),
    fetchAllNewstype: builder.query({ query: () => "news-type-link/get-all" }),
    createNewsType: builder.mutation({
      query: (newsData) => ({
        url: "news-type-link/create",
        method: "POST",
        body: newsData,
      }),
    }),
    updateNewsType: builder.mutation({
      query: ({ id, newsData }) => ({
        url: `news-type-link/update/${id}`,
        method: "PUT",
        body: newsData,
      }),
    }),
    deleteNewsType: builder.mutation({
      query: (id) => ({ url: `news-type-link/delete/${id}`, method: "DELETE" }),
    }),
    createNews: builder.mutation({
      query: (newsData) => ({
        url: "news/create",
        method: "POST",
        body: newsData,
      }),
    }),
    updateNews: builder.mutation({
      query: ({ id, newsData }) => ({
        url: `news/update/${id}`,
        method: "PUT",
        body: newsData,
      }),
    }),
    deleteNews: builder.mutation({
      query: (id) => ({ url: `news/delete/${id}`, method: "DELETE" }),
    }),
    filterNewsByUser: builder.query({
      query: (filterData) => ({
        url: "news/user-based-filter",
        method: "POST",
        body: filterData,
      }),
    }),
    fetchAutoNewsStatistic: builder.query({
      query: () => "stats/cronjob/query-stats-data",
    }),
    fetchAllBanners: builder.query({
      query: () => "banners/get-all",
    }),
    fetchAllActiveBanners: builder.query({
      query: () => "banners/get-all-active",
    }),
    fetchBannerVotes: builder.query({
      query: (id) => `votes/banner-votes?bannerId=${id}`,
    }),
    fetchSingleBanner: builder.query({
      query: (id) => `banners/get-single/${id}`,
    }),
    createBanner: builder.mutation({
      query: (data) => ({
        url: "banners/create",
        method: "POST",
        body: data, // Assuming data is already FormData
      }),
    }),

    updateBanner: builder.mutation({
      query: ({ id, data }) => {
        return {
          url: `banners/update/${id}`,
          method: "PUT",
          body: data,
        };
      },
    }),
    deleteBanner: builder.mutation({
      query: (id) => ({
        url: `banners/delete/${id}`,
        method: "DELETE",
      }),
    }),
    createVote: builder.mutation({
      query: (vote) => ({
        url: "votes/create",
        method: "POST",
        body: vote,
      }),
    }),

    fetchProfiles: builder.query({
      query: () => ({
        url: "/auth/user-profile",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": JSON.parse(localStorage.getItem("token"))?.token,
          "secret-key": process.env.REACT_APP_SECRET_KEY,
        },
      }),
    }),

    addUser: builder.mutation({
      query: (newUser) => ({
        url: "auth/signup",
        method: "POST",
        body: newUser,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateUser: builder.mutation({
      query: (updatedUser) => ({
        url: "auth/update-profile",
        method: "PUT",
        body: updatedUser,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    login: builder.mutation({
      query: (loginCred) => ({
        url: "auth/login",
        method: "POST",
        body: loginCred,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    verifyOtp: builder.mutation({
      query: (otpCred) => ({
        url: "auth/verify-otp",
        method: "POST",
        body: otpCred,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    fetchAllLiveScores: builder.query({
      query: () => "live-scores/get-all",
    }),
    fetchAllLiveSchoolScores: builder.query({
      query: (schoolId) => `live-scores/get-all?schoolid=${schoolId}`,
    }),
    fetchSingleLiveScore: builder.query({
      query: (id) => `live-scores/get-single/${id}`,
    }),
    createLiveScore: builder.mutation({
      query: (liveScoreData) => ({
        url: "live-scores/create",
        method: "POST",
        body: liveScoreData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateLiveScore: builder.mutation({
      query: ({ id, liveScoreData }) => ({
        url: `live-scores/update/${id}`,
        method: "PUT",
        body: liveScoreData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteLiveScore: builder.mutation({
      query: (id) => ({
        url: `live-scores/delete/${id}`,
        method: "DELETE",
      }),
    }),
    // User Management
    fetchUsers: builder.query({
      query: () => `crud/${secretKey}/users/get-all`,
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `crud/${secretKey}/users/delete/${id}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }),
    }),

    // Key Management
    fetchKeys: builder.query({ query: () => "keys/get-all" }),
    createKey: builder.mutation({
      query: (newKey) => ({
        url: "keys/create",
        method: "POST",
        body: newKey,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteKey: builder.mutation({
      query: (id) => ({
        url: `keys/delete/${id}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }),
    }),

    fetchGameStates: builder.query({
      query: (id) => `play-game/game-stats/${id}`,
    }),
    // get api for near by
    fetchNearByFans: builder.query({
      query: (id) => `auth/nearby-users/${id}`,
    }),
    // near by code ends here
    fetchGamesNews: builder.query({
      query: (data) => ({
        url: "news/user-based-news",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    fetchLiveScores: builder.query({ query: () => "live-scores/get-all" }),

    invitationLink: builder.mutation({
      query: ({ randomId, userFirstname, userLastname }) => ({
        url: `invitation/${randomId}?firstName=${encodeURIComponent(
          userFirstname
        )}&Lastname=${encodeURIComponent(userLastname)}`,
        method: "PUT",
        body: "",
        headers: { "Content-Type": "application/json" },
      }),
    }),

    fetchForum: builder.query({ query: () => "forums/get-all" }),
    fetchForumSports: builder.query({
      query: (sportsId) => `forums/get-all/?sportsID=${sportsId}`,
    }),
    fetchForumdetail: builder.query({
      query: (_id) => `forums/get-single/${_id}`,
    }),
    fetchAllNillNews: builder.query({ query: () => "nil-news/get-all" }),
    fetchAllNewsType: builder.query({
      query: (id) => `news-type-link/getAllforUser/${id}`,
    }),

    fetchAllNewsUniType: builder.query({
      query: (College, sport) => {
        const encodedCollege = encodeURIComponent(College || "");
        const encodedSport = encodeURIComponent(sport || "");
        return `news/fetch-and-save-google-search-news?college=${encodedCollege}&query=${encodedSport}&max_result=10`;
      },
    }),

    fetchSingleNillNews: builder.query({
      query: (id) => `nil-news/get-single/${id}`,
    }),
    createNillNews: builder.mutation({
      query: (newsData) => ({
        url: "nil-news/create",
        method: "POST",
        body: newsData,
      }),
    }),
    updateNillNews: builder.mutation({
      query: ({ id, newsData }) => ({
        url: `nil-news/update/${id}`,
        method: "PUT",
        body: newsData,
      }),
    }),
    deleteNillNews: builder.mutation({
      query: (id) => ({ url: `nil-news/delete/${id}`, method: "DELETE" }),
    }),
    filterNillNewsByUser: builder.query({
      query: (filterData) => ({
        url: "nil-news/user-based-filter",
        method: "POST",
        body: filterData,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    fetchColleges: builder.query({ query: () => "university/get-all" }),
    fetchAthletes: builder.query({ query: () => "athletes/get-all" }),
    updateUniversity: builder.mutation({
      query: ({ data, id }) => ({
        url: `university/update/${id}`,
        method: "PUT",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateAthlete: builder.mutation({
      query: ({ data, id }) => ({
        url: `athletes/update/${id}`,
        method: "PUT",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    createUniversity: builder.mutation({
      query: ({ data }) => ({
        url: "university/create",
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    createAthlete: builder.mutation({
      query: ({ data }) => ({
        url: "athletes/create",
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteUniversity: builder.mutation({
      query: ({ id }) => ({
        url: `university/delete/${id}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteAthlete: builder.mutation({
      query: ({ id }) => ({
        url: `athletes/delete/${id}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }),
    }),

    fetchAllRecruitNews: builder.query({ query: () => "recruit-news/get-all" }),
    fetchSingleRecruitNews: builder.query({
      query: (id) => `recruit-news/get-single/${id}`,
    }),
    createRecruitNews: builder.mutation({
      query: (newsData) => ({
        url: "recruit-news/create",
        method: "POST",
        body: newsData,
      }),
    }),
    updateRecruitNews: builder.mutation({
      query: ({ id, newsData }) => ({
        url: `recruit-news/update/${id}`,
        method: "PUT",
        body: newsData,
      }),
    }),
    deleteRecruitNews: builder.mutation({
      query: (id) => ({ url: `recruit-news/delete/${id}`, method: "DELETE" }),
    }),
    filterRecruitNewsByUser: builder.query({
      query: (filterData) => ({
        url: "recruit-news/user-based-filter",
        method: "POST",
        body: filterData,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    // Transfer News Endpoints
    fetchAllTransferNews: builder.query({
      query: () => "transfer-news/get-all",
    }),
    fetchSingleTransferNews: builder.query({
      query: (id) => `transfer-news/get-single/${id}`,
    }),
    createTransferNews: builder.mutation({
      query: (newsData) => ({
        url: "transfer-news/create",
        method: "POST",
        body: newsData,
      }),
    }),
    updateTransferNews: builder.mutation({
      query: ({ id, newsData }) => ({
        url: `transfer-news/update/${id}`,
        method: "PUT",
        body: newsData,
      }),
    }),
    deleteTransferNews: builder.mutation({
      query: (id) => ({ url: `transfer-news/delete/${id}`, method: "DELETE" }),
    }),
    filterTransferNewsByUser: builder.query({
      query: (filterData) => ({
        url: "transfer-news/user-based-filter",
        method: "POST",
        body: filterData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    fetchAllRaffles: builder.query({
      query: () => "raffles/get-all",
    }),
    fetchSingleRaffle: builder.query({
      query: (id) => `raffles/get-single/${id}`,
    }),
    createRaffle: builder.mutation({
      query: ({
        logoFile,
        sponsorLogoImage,
        entryDate,
        entryCutOff,
        description,
        narrative,
        sponsorName,
        sponsorLink,
        notes,
        sponsorId,
        collegeId,
        adminName,
        adminEmail,
        adminPhone,
        title,
      }) => {
        const formData = new FormData();
        formData.append("logoFile", logoFile);
        formData.append("sponsorLogoImage", sponsorLogoImage);
        formData.append("entryDate", entryDate);
        formData.append("entryCutOff", entryCutOff);
        formData.append("description", description);
        formData.append("narrative", narrative);
        formData.append("sponsorName", sponsorName);
        formData.append("sponsorLink", sponsorLink);
        formData.append("notes", notes);
        formData.append("sponsorId", sponsorId);
        formData.append("collegeId", collegeId);
        formData.append("adminName", adminName);
        formData.append("adminEmail", adminEmail);
        formData.append("adminPhone", adminPhone);
        formData.append("title", title);

        return {
          url: "raffles/create",
          method: "POST",
          body: formData,
        };
      },
    }),

    updateRaffle: builder.mutation({
      query: ({ id, ...data }) => {
        const formData = new FormData();
        Object.keys(data).forEach((key) => {
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          } else {
            formData.append(key, data[key] || "");
          }
        });

        return {
          url: `raffles/update/${id}`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    deleteRaffle: builder.mutation({
      query: (id) => ({
        url: `raffles/delete/${id}`,
        method: "DELETE",
      }),
    }),
    fetchAllAdvertisers: builder.query({
      query: () => "advertisers/get-all",
    }),
    fetchSingleAdvertiser: builder.query({
      query: (id) => `advertisers/get-single/${id}`,
    }),
    fetchSingleCustomAdvertiser: builder.query({
      query: (college) => `advertisers/get-single-custom/${college}`,
    }),
    createAdvertiser: builder.mutation({
      query: (advertiserData) => ({
        url: "advertisers/create",
        method: "POST",
        body: advertiserData,
      }),
    }),

    updateAdvertiser: builder.mutation({
      query: ({ id, ...advertiserData }) => ({
        url: `advertisers/update/${id}`,
        method: "PUT",
        body: advertiserData,
      }),
    }),

    deleteAdvertiser: builder.mutation({
      query: (id) => ({
        url: `advertisers/delete/${id}`,
        method: "DELETE",
      }),
    }),
    fetchAllsmtpSetting: builder.query({
      query: () => "app-setting/get/smtp_setting",
    }),
    updateSmtp: builder.mutation({
      query: ({ smtpData }) => ({
        url: `app-setting/smtp/update`,
        method: "PUT",
        body: smtpData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    fetchAllsmtpStatics: builder.query({
      query: () => "app-setting/get/smtp_statistics",
    }),
    fetchAllGames: builder.query({
      query: () => "play-game/get-all",
    }),
    fetchSingleGame: builder.query({
      query: (id) => `play-game/get-single/${id}`,
    }),
    updateGame: builder.mutation({
      query: ({ id, gameData }) => ({
        url: `play-game/update/${id}`,
        method: "PUT",
        body: gameData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteGame: builder.mutation({
      query: (id) => ({
        url: `play-game/delete/${id}`,
        method: "DELETE",
      }),
    }),
    fetchGameStats: builder.query({
      query: (id) => `play-game/game-stats/${id}`,
    }),
    createUniversitySport: builder.mutation({
      query: (data) => ({
        url: "university-sports/create",
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    fetchForums: builder.query({
      query: () => "forums/get-all",
    }),
    fetchSingleForum: builder.query({
      query: (id) => `forums/get-single/${id}`,
    }),
    createForum: builder.mutation({
      query: (forumData) => ({
        url: "forums/create",
        method: "POST",
        body: forumData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateForum: builder.mutation({
      query: ({ id, forumData }) => ({
        url: `forums/update/${id}`,
        method: "PUT",
        body: forumData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteForum: builder.mutation({
      query: (id) => ({
        url: `forums/delete/${id}`,
        method: "DELETE",
      }),
    }),

    fetchForumMembers: builder.query({
      query: (forumId) => `forums/${forumId}/members/get-all`,
    }),
    addForumMember: builder.mutation({
      query: ({ forumId, memberData }) => ({
        url: `forums/${forumId}/members/create`,
        method: "POST",
        body: { ...memberData, status: false },
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateForumMember: builder.mutation({
      query: ({ forumId, memberId, memberData }) => ({
        url: `forums/${forumId}/members/update/${memberId}`,
        method: "PUT",
        body: memberData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteForumMember: builder.mutation({
      query: ({ forumId, memberId }) => ({
        url: `forums/${forumId}/members/delete/${memberId}`,
        method: "DELETE",
      }),
    }),

    fetchForumMessages: builder.query({
      query: (forumId) => `forums/${forumId}/messages/get-all`,
    }),
    createForumMessage: builder.mutation({
      query: ({ forumId, messageData }) => ({
        url: `forums/${forumId}/messages/create`,
        method: "POST",
        body: messageData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateForumMessage: builder.mutation({
      query: ({ forumId, messageId, messageData }) => ({
        url: `forums/${forumId}/messages/update/${messageId}`,
        method: "PUT",
        body: messageData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteForumMessage: builder.mutation({
      query: ({ forumId, messageId }) => ({
        url: `forums/${forumId}/messages/delete/${messageId}`,
        method: "DELETE",
      }),
    }),
    fetchAllUniversitySports: builder.query({
      query: () => "university-sports/get-all",
    }),
    fetchAllStats: builder.query({
      query: () => "stats/get-all",
    }),
    // Sports Endpoints
    fetchAllSports: builder.query({
      query: () => "sports/get-all",
    }),
    fetchSingleSport: builder.query({
      query: (id) => `sports/get-single/${id}`,
    }),
    createSport: builder.mutation({
      query: (sportData) => ({
        url: "sports/create",
        method: "POST",
        body: sportData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    updateSport: builder.mutation({
      query: ({ id, sportData }) => ({
        url: `sports/update/${id}`,
        method: "PUT",
        body: sportData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteSport: builder.mutation({
      query: (id) => ({
        url: `sports/delete/${id}`,
        method: "DELETE",
      }),
    }),
    createTeam: builder.mutation({
      query: (TeamData) => ({
        url: "/teams/create",
        method: "POST",
        body: TeamData,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    fetchAllTeams: builder.query({
      query: () => "/teams/get-all",
    }),
    fetchAllTeamsByUser: builder.query({
      query: (userID) => `/teams/getAllbyUser/${userID}`,
    }),
    addTeamMember: builder.mutation({
      query: ({ TeamID, newMembers }) => ({
        url: `/teams/addteamMembers/${TeamID}`,
        method: "PUT",
        body: JSON.stringify({ newMembers }),
        headers: { "Content-Type": "application/json" },
      }),
    }),
    deleteTeamMember: builder.mutation({
      query: ({ TeamId, Memberid }) => ({
        url: `/teams/removeteamMember/${TeamId}/${Memberid}`,
        method: "PUT",
      }),
    }),
    fetchUserList: builder.query({
      query: (userId) => `/messages/user-msglist?userId=${userId}`,
    }),
    fetchConversation: builder.query({
      query: ({ userId, otherId }) =>
        `/messages/user-conversation?userId=${userId}&otherId=${otherId}`,
    }),
    fetchAllAdminmessages: builder.query({
      query: () => "messages/messagestoadmin",
    }),
    sendMessage: builder.mutation({
      query: (messageData) => ({
        url: "/messages/create",
        method: "POST",
        body: messageData,
      }),
    }),
  }),
});

export const {
  useFetchAutoNewsStatisticQuery,
  useFetchAllGamesQuery,
  useFetchAllPaymentQuery,
  useFetchSingleGameQuery,
  useUpdateGameMutation,
  useDeleteGameMutation,
  useFetchGameStatsQuery,
  useFetchAllNewsNetQuery,
  useFetchAllStatsQuery,
  useFetchAllNewsQuery,
  useFetchSingleNewsQuery,
  useFetchBannerVotesQuery,
  useFetchAllNewstypeQuery,
  useCreateNewsNetMutation,
  useUpdateNewsNetMutation,
  useDeleteNewsNetMutation,
  useCreateNewsTypeMutation,
  useUpdateNewsTypeMutation,
  useDeleteNewsTypeMutation,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
  useFilterNewsByUserQuery,
  useFetchProfilesQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useLoginMutation,
  useFetchForumsQuery,
  useFetchSingleForumQuery,
  useCreateForumMutation,
  useUpdateForumMutation,
  useDeleteForumMutation,
  useFetchForumMembersQuery,
  useAddForumMemberMutation,
  useDeleteForumMemberMutation,
  useFetchForumMessagesQuery,
  useCreateForumMessageMutation,
  useDeleteForumMessageMutation,
  useVerifyOtpMutation,
  useCreateUniversitySportMutation,
  useFetchAllUniversitySportsQuery,
  useFetchUsersQuery,
  useDeleteUserMutation,
  useFetchAllsmtpSettingQuery,
  useFetchAllsmtpStaticsQuery,
  useUpdateSmtpMutation,
  useFetchGameStatesQuery,
  useFetchNearByFansQuery,
  useFetchGamesNewsQuery,
  useFetchLiveScoresQuery,
  useFetchAllLiveSchoolScoresQuery,
  useFetchAllAdvertisersQuery,
  useFetchAllAdminmessagesQuery,
  useFetchUserListQuery,
  useFetchConversationQuery,
  useSendMessageMutation,
  useFetchSingleAdvertiserQuery,
  useFetchSingleCustomAdvertiserQuery,
  useCreateAdvertiserMutation,
  useUpdateAdvertiserMutation,
  useDeleteAdvertiserMutation,
  useInvitationLinkMutation,
  useFetchForumQuery,
  useFetchForumdetailQuery,
  useFetchForumSportsQuery,
  useFetchCollegesQuery,
  useUpdateUniversityMutation,
  useDeleteUniversityMutation,
  useCreateUniversityMutation,
  useCreateAthleteMutation,
  useUpdateAthleteMutation,
  useFetchAthletesQuery,
  useDeleteAthleteMutation,
  useFetchKeysQuery,
  useCreateKeyMutation,
  useDeleteKeyMutation,
  useFetchAllRecruitNewsQuery,
  useFetchSingleRecruitNewsQuery,
  useCreateRecruitNewsMutation,
  useUpdateRecruitNewsMutation,
  useDeleteRecruitNewsMutation,
  useFilterRecruitNewsByUserQuery,
  useFetchAllTransferNewsQuery,
  useFetchSingleTransferNewsQuery,
  useCreateTransferNewsMutation,
  useUpdateTransferNewsMutation,
  useDeleteTransferNewsMutation,
  useFilterTransferNewsByUserQuery,
  useFetchAllNillNewsQuery,
  useFetchAllNewsTypeQuery,
  useFetchAllNewsUniTypeQuery,
  useFetchSingleNillNewsQuery,
  useCreateNillNewsMutation,
  useUpdateNillNewsMutation,
  useDeleteNillNewsMutation,
  useFilterNillNewsByUserQuery,
  useFetchAllBannersQuery,
  useFetchAllActiveBannersQuery,
  useFetchSingleBannerQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useCreateVoteMutation,
  useFetchAllLiveScoresQuery,
  useFetchSingleLiveScoreQuery,
  useCreateLiveScoreMutation,
  useUpdateLiveScoreMutation,
  useDeleteLiveScoreMutation,
  useFetchAllRafflesQuery,
  useFetchSingleRaffleQuery,
  useCreateRaffleMutation,
  useUpdateRaffleMutation,
  useDeleteRaffleMutation,
  useDeleteBannerMutation,
  useFetchAllSportsQuery,
  useFetchSingleSportQuery,
  useCreateSportMutation,
  useUpdateSportMutation,
  useDeleteSportMutation,
  useCreateTeamMutation,
  useFetchAllTeamsQuery,
  useDeleteTeamMemberMutation,
  useFetchAllTeamsByUserQuery,
  useAddTeamMemberMutation,
  useUpdateForumMemberMutation,
  useCreateForumMemberMutation,
} = apiSlice;
